import {
  Alert,
  Box,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconAlertTriangle, IconBuildingBank, IconCircleCheck, IconInfoCircle, IconShieldCheck } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import useSWR from "swr";
import { AccountApi, BillingAccountApi } from "../../apis";
import type { BankAccountPurpose } from "../../apis/account";
import { openBankConsent } from "../../utils/bank-consent";
import { Message } from "../../utils/message";
import { PURPOSE_LABELS } from "../../utils/bank-account-labels";

// The payment module's bank verification only supports these codes (BANK_NAMES).
const SUPPORTED_BANKS = ["GOLOMT", "BOGD", "DEHUB"];

const PURPOSE_OPTIONS = [
  { value: "RECEIVE", label: PURPOSE_LABELS.RECEIVE },
  { value: "PAY", label: PURPOSE_LABELS.PAY },
  { value: "BOTH", label: PURPOSE_LABELS.BOTH },
];

interface BankRef {
  code: string;
  name: string;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/**
 * Single-screen "Данс нэмэх" flow (spec §3.2 / mockup backoffice-accounts.html),
 * reusing de-payment's Golomt verification: bank + 10-digit number →
 * /pay/bank_account/check → consent popup → confirm; the verified block (read-only
 * name/currency + short name + purpose + default) is revealed below the still-visible
 * bank/number fields, then persisted to de-billing.
 *
 * The verification is scoped to a businessId. A tenant admin has none on their JWT,
 * so myBusinessId comes (invisibly) from the tenant's saved operating business
 * (/bil/my/billing-account); it is set elsewhere (not here).
 */
export function AddBankAccountDrawer({ opened, onClose, onCreated }: Props) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bankNames = useSelector((s: any) => s.general?.bankNames ?? []) as BankRef[];
  const bankOptions = useMemo(
    () =>
      bankNames
        .filter((b) => SUPPORTED_BANKS.includes(b.code))
        .map((b) => ({ value: b.code, label: b.name })),
    [bankNames]
  );

  const { data: billingAccount } = useSWR(
    opened ? "swr.my.billing-account" : null,
    () => BillingAccountApi.getMy()
  );
  const myBusinessId = billingAccount?.operatingBusinessId ?? null;

  const [loading, setLoading] = useState(false);
  const [bankCode, setBankCode] = useState<string | null>(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [verified, setVerified] = useState<{ accountName: string; currency: string } | null>(null);
  const [shortName, setShortName] = useState("");
  const [purpose, setPurpose] = useState<BankAccountPurpose>("RECEIVE");
  const [isDefault, setIsDefault] = useState(false);
  const [errors, setErrors] = useState<{ bankCode?: string; accountNumber?: string; shortName?: string }>({});

  const reset = () => {
    setLoading(false); setBankCode(null); setAccountNumber("");
    setVerified(null); setShortName(""); setPurpose("RECEIVE"); setIsDefault(false); setErrors({});
  };
  const close = () => { reset(); onClose(); };

  const bankLabel = bankNames.find((b) => b.code === bankCode)?.name ?? null;

  // Poll the payment list endpoint to confirm registration after consent (re-running
  // check would throw "already registered"). Mirrors de-payment-admin.
  const findRegistered = async (code: string, number: string) => {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await AccountApi.listPay({ bankName: code, number, myBusinessId: myBusinessId ?? undefined });
        const found = (res?.rows ?? []).find((a) => a.bankName === code && a.number === number);
        if (found) return found;
      } catch {
        return null;
      }
      if (attempt < 2) await new Promise((r) => setTimeout(r, 700));
    }
    return null;
  };

  const onVerify = async () => {
    const e: typeof errors = {};
    if (!bankCode) e.bankCode = "Банк сонгоно уу.";
    if (!/^\d{10}$/.test(accountNumber)) e.accountNumber = "Дансны дугаар 10 оронтой байх ёстой.";
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    if (!myBusinessId) return;

    setLoading(true);
    try {
      const res = await AccountApi.checkPay({ bankName: bankCode!, number: accountNumber, myBusinessId });
      // No consent URL (BOGD/DEHUB): the owner name comes back directly.
      if (!res?.url) {
        setVerified({ accountName: res.accountName ?? "", currency: res.currency ?? "MNT" });
        setLoading(false);
        return;
      }
      // Golomt: open consent, then confirm via the list endpoint.
      Message.success("Банкны баталгаажуулалтыг цонхонд гүйцэтгэнэ үү.");
      await openBankConsent(res.url, "Банкаар баталгаажуулах");
      const found = await findRegistered(bankCode!, accountNumber);
      if (!found) {
        Message.error("Банкны баталгаажуулалт дуусаагүй байна. Дахин оролдоно уу.");
        setLoading(false);
        return;
      }
      setVerified({ accountName: found.name ?? "", currency: found.currency ?? "MNT" });
      setLoading(false);
    } catch {
      // http-handler surfaces the error toast; just unlock the form.
      setLoading(false);
    }
  };

  const onSave = async () => {
    const e: typeof errors = {};
    if (!shortName.trim()) e.shortName = "Дансны товч нэр шаардлагатай.";
    setErrors(e);
    if (Object.keys(e).length > 0 || !verified || !bankCode) return;

    setLoading(true);
    try {
      await AccountApi.create({
        bankCode,
        bankName     : bankLabel,
        accountNumber,
        accountName  : verified.accountName,
        shortName    : shortName.trim(),
        currency     : verified.currency || "MNT",
        purpose,
        isDefault
      });
      Message.success("Данс амжилттай нэмэгдлээ.");
      onCreated();
      close();
    } catch {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      size={520}
      title={
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon size={38} radius="md" variant="light" color="brand">
            <IconBuildingBank size={20} />
          </ThemeIcon>
          <Box>
            <Title order={4}>Шинэ данс нэмэх</Title>
            <Text size="sm" c="dimmed">Банкаар баталгаажуулж бүртгэнэ</Text>
          </Box>
        </Group>
      }>
      <Stack gap="md">
        {billingAccount && !myBusinessId && (
          <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />}>
            Байгууллага тохируулаагүй байна.
          </Alert>
        )}
        <Select
          label="Банк"
          placeholder="Банк сонгох"
          required
          data={bankOptions}
          value={bankCode}
          onChange={setBankCode}
          error={errors.bankCode}
          disabled={!!verified}
        />
        <Group align="flex-end" gap="sm" wrap="nowrap">
          <TextInput
            style={{ flex: 1 }}
            label="Дансны дугаар"
            placeholder="10 оронтой дугаар"
            description="Зөвхөн өөрийн нэр дээрх данс"
            required
            value={accountNumber}
            maxLength={10}
            onChange={(ev) => setAccountNumber(ev.currentTarget.value.replace(/\D/g, ""))}
            error={errors.accountNumber}
            disabled={!!verified}
          />
          {!verified && (
            <Button
              leftSection={<IconShieldCheck size={16} />}
              onClick={onVerify}
              loading={loading}
              disabled={!myBusinessId}>
              Баталгаажуулах
            </Button>
          )}
        </Group>

        {verified && (
          <Stack gap="md">
            <Alert color="teal" variant="light" icon={<IconCircleCheck size={16} />}>
              Банкаар баталгаажлаа — дансны эзэмшигч таталаа
            </Alert>
            <TextInput label="Дансны нэр" value={verified.accountName} readOnly disabled />
            <Group grow>
              <TextInput label="Валют" value={verified.currency || "MNT"} readOnly disabled />
              <TextInput
                label="Дансны товч нэр"
                placeholder="Жишээ: Үндсэн данс"
                required
                value={shortName}
                onChange={(ev) => setShortName(ev.currentTarget.value)}
                error={errors.shortName}
              />
            </Group>
            <Box>
              <Text size="sm" fw={500} mb={6}>Зориулалт <Text span c="red">*</Text></Text>
              <SegmentedControl
                fullWidth
                value={purpose}
                onChange={(v) => setPurpose(v as BankAccountPurpose)}
                data={PURPOSE_OPTIONS}
              />
              <Text size="xs" c="dimmed" mt={6}>
                Шимтгэлийн орлого энэ данс руу орох / платформын төлбөр энэ данснаас гарах эсэх
              </Text>
            </Box>
            <Switch
              label="Үндсэн данс болгох"
              description="Нэг валютад нэг л үндсэн данс. Шимтгэлийн орлого үндсэн «Орлого хүлээн авах» данс руу орно."
              checked={isDefault}
              onChange={(ev) => setIsDefault(ev.currentTarget.checked)}
            />
            <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
              Дансны баталгаажуулалт банкны цонхоор хийгдэнэ. Баталгаажаагүй данс руу орлого хүлээн авах боломжгүй.
            </Alert>
          </Stack>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={close} disabled={loading}>Болих</Button>
          {verified && (
            <Button color="green" onClick={onSave} loading={loading}>Хадгалах</Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
